import {
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import { AutocompleteRenderOptionState } from '@material-ui/lab';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import AutoComplete from '../../../../components/AutoComplete';
import CustomDialog from '../../../../components/CustomDialog';
import { useLoadingDialog } from '../../../../hooks/backdrop';
import { useFeedback } from '../../../../hooks/feedback';
import { CondominiumDTO } from '../../../../models/dto/CondominiumDTO';
import { EmployeeDTO } from '../../../../models/dto/EmployeeDTO';
import { AppEnum } from '../../../../models/enum/AppEnum';
import {
  SendNoticeRequestData,
  sendNotices,
} from '../../../../services/personnelDepartment.service';
import { AppState } from '../../../../store';
import {
  ALL_CONDOMINIUMS,
  CondominiumReducerData,
} from '../../../../store/reducers/condominiumReducer';
import {
  ALL_EMPLOYEES,
  EmployeesReducerData,
} from '../../../../store/reducers/employeesReducer';

type SendNoticesProps = {
  open: boolean;
  handleCloseDialog: () => void;
};

const CONDOMINIUM_SENDING_TYPE = 'CONDOMINIUMS';
const EMPLOYEES_SENDING_TYPE = 'EMPLOYEES';

const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    chips: {
      display: 'flex',
      flexWrap: 'wrap',
    },
    fieldLabelText: {
      fontWeight: 700,
    },
  }),
);

const SendNoticesDialog: React.FC<SendNoticesProps> = ({
  open,
  handleCloseDialog: handleCloseModal,
}) => {
  const classes = useStyles();
  const { addFeedback } = useFeedback();
  const { handleLoadingDialog } = useLoadingDialog();
  const [sendingType, setSendingType] = useState<string>(
    EMPLOYEES_SENDING_TYPE,
  );
  const [noticeTitle, setNoticeTitle] = useState<string>('');
  const [noticeText, setNoticeText] = useState<string>('');
  const [referencesSelected, setReferencesSelected] = useState<
    CondominiumDTO[]
  >([]);
  const [employeesSelected, setEmployeesSelected] = useState<EmployeeDTO[]>([]);
  const [hasSelectAllEmployees, setHasSelectAllEmployees] = useState<boolean>(
    false,
  );
  const [hasSelectAllCondominiums, setHasSelectAllCondominiums] = useState<
    boolean
  >(false);

  const { condominiums, loading }: CondominiumReducerData = useSelector(
    (state: AppState) => state.condominiumReducer,
  );

  const { employees }: EmployeesReducerData = useSelector(
    (state: AppState) => state.employeesReducer,
  );

  const handleCondominiumsSelect = (
    _event: React.ChangeEvent<any>,
    values: CondominiumDTO[],
  ) => {
    const optionSelectedText = _event.currentTarget.innerText;
    const isSelectAllOption = optionSelectedText.includes('Todos');
    const alreadyHaveValue = referencesSelected.length > 0;

    if (isSelectAllOption) {
      if (!alreadyHaveValue && !hasSelectAllCondominiums) {
        setReferencesSelected([ALL_CONDOMINIUMS, ...condominiums]);
      } else if (alreadyHaveValue && !hasSelectAllCondominiums) {
        const condominiumsUnselected = condominiums.filter(
          condominium => !referencesSelected.includes(condominium),
        );
        setReferencesSelected([
          ...referencesSelected,
          ...condominiumsUnselected,
          ALL_CONDOMINIUMS,
        ]);
      } else {
        setReferencesSelected([]);
      }
    } else if (hasSelectAllCondominiums) {
      const newArray = referencesSelected.filter(
        condominium =>
          !optionSelectedText.includes(condominium.nome) &&
          condominium.referencia !== 0,
      );
      setReferencesSelected(newArray);
    } else {
      setReferencesSelected(values);
    }
  };

  const handleEmployeesSelect = (
    _event: React.ChangeEvent<Record<string, any>>,
    values: EmployeeDTO[],
  ) => {
    const optionSelectedText = _event.currentTarget.innerText;
    const isSelectAllOption = optionSelectedText.includes('Todos');
    const alreadyHaveValue = employeesSelected.length > 0;

    if (isSelectAllOption) {
      if (!alreadyHaveValue && !hasSelectAllEmployees) {
        setEmployeesSelected([ALL_EMPLOYEES, ...employees]);
      } else if (alreadyHaveValue && !hasSelectAllEmployees) {
        const employeesUnselected = employees.filter(
          employee => !employeesSelected.includes(employee),
        );
        setEmployeesSelected([
          ...employeesSelected,
          ...employeesUnselected,
          ALL_EMPLOYEES,
        ]);
      } else {
        setEmployeesSelected([]);
      }
    } else if (hasSelectAllEmployees) {
      const newArray = employeesSelected.filter(
        employee =>
          !optionSelectedText.includes(employee.nome) &&
          employee.referencia !== 0,
      );
      setEmployeesSelected(newArray);
    } else {
      setEmployeesSelected(values);
    }
  };

  const handleEmployeesTagRemoveSelection = (employee: EmployeeDTO) => {
    const newArray = employeesSelected.filter(
      employeeSelected =>
        employeeSelected.numeroCadastro !== employee.numeroCadastro,
    );
    setEmployeesSelected(newArray);
  };

  const handleCondominiumsTagRemoveSelection = (
    condominium: CondominiumDTO,
  ) => {
    const newArray = referencesSelected.filter(
      referenceSelected =>
        referenceSelected.referencia !== condominium.referencia,
    );
    setReferencesSelected(newArray);
  };

  const buildNoticeRequestData = () => {
    const data: SendNoticeRequestData[] = [];

    if (sendingType === EMPLOYEES_SENDING_TYPE) {
      employeesSelected
        .filter(employee => employee.referencia !== 0)
        .forEach(employee => {
          data.push({
            identifier: employee.numeroCadastro,
            reference: employee.referencia,
            title: noticeTitle,
            message: noticeText,
            type: AppEnum.APP_PONTO.description,
          });
        });
    } else {
      referencesSelected
        .filter(condominium => condominium.referencia !== 0)
        .forEach(condominium => {
          data.push({
            reference: condominium.referencia,
            title: noticeTitle,
            message: noticeText,
            type: AppEnum.APP_PONTO.description,
          });
        });
    }

    return data;
  };

  const submit = async () => {
    handleLoadingDialog(true);

    const data = buildNoticeRequestData();

    try {
      await sendNotices(data);
      addFeedback({
        type: 'success',
        closeTimeout: 7000,
        description: 'Aviso enviado com sucesso!',
        onClose: () => handleCloseModal(),
      });
      setNoticeText('');
      setNoticeTitle('');
      setEmployeesSelected([]);
      setReferencesSelected([]);
    } catch (error) {
      console.log(error);
      addFeedback({
        type: 'error',
        description: 'Ocorreu um erro ao enviar o aviso.',
      });
    } finally {
      handleLoadingDialog(false);
    }
  };

  useEffect(() => {
    setHasSelectAllEmployees(
      employeesSelected.some(employee => employee.referencia === 0),
    );
  }, [employeesSelected]);

  useEffect(() => {
    setHasSelectAllCondominiums(
      referencesSelected.some(reference => reference.referencia === 0),
    );
  }, [referencesSelected]);

  const selectCondominiumsField = (
    <>
      <Grid item xs={12} sm={3}>
        <Typography
          className={classes.fieldLabelText}
          color="textPrimary"
          variant="body1"
        >
          Condomínio(s):
        </Typography>
      </Grid>
      <Grid item xs={12} sm={9}>
        <AutoComplete
          loading={loading}
          options={[
            ALL_CONDOMINIUMS,
            ...condominiums.sort((a, b) =>
              a.referencia > b.referencia ? 1 : -1,
            ),
          ]}
          getOptionLabel={(option: CondominiumDTO) =>
            `${option.referencia !== 0 ? `${option.referencia} - ` : ''}${
              option.nome
            }`
          }
          multiple
          limitTags={2}
          value={referencesSelected}
          renderTags={(value: CondominiumDTO[]) => {
            return value.some(condominium => condominium.referencia === 0) ? (
              <Chip
                label="Todos"
                onDelete={() => setReferencesSelected([])}
                color="default"
              />
            ) : (
              value.map(condominium => (
                <Chip
                  label={`${condominium.referencia} - ${condominium.nome}`}
                  onDelete={() =>
                    handleCondominiumsTagRemoveSelection(condominium)
                  }
                  color="default"
                />
              ))
            );
          }}
          onChange={handleCondominiumsSelect}
          placeholder="Selecione o(s) Condomínio(s)"
          renderOption={(
            option: CondominiumDTO,
            state: AutocompleteRenderOptionState,
          ) => {
            return (
              <div id={state.inputValue}>
                <Checkbox
                  color="primary"
                  icon={<CheckBoxOutlineBlankIcon />}
                  checkedIcon={<CheckBoxIcon />}
                  style={{ marginRight: 8 }}
                  checked={state.selected}
                />
                {`${option.referencia !== 0 ? `${option.referencia} - ` : ''}${
                  option.nome
                }`}
              </div>
            );
          }}
        />
      </Grid>
    </>
  );

  const selectEmployeesField = (
    <>
      <Grid item xs={12} sm={3}>
        <Typography
          className={classes.fieldLabelText}
          color="textPrimary"
          variant="body1"
        >
          Funcionário(s):
        </Typography>
      </Grid>
      <Grid item xs={12} sm={9}>
        <AutoComplete
          loading={loading}
          value={employeesSelected}
          options={[
            ALL_EMPLOYEES,
            ...employees.sort((a, b) => (a.nome > b.nome ? 1 : -1)),
          ]}
          getOptionLabel={(option: EmployeeDTO) =>
            `${
              option.numeroCadastro !== 0 ? `${option.numeroCadastro} - ` : ''
            }${option.nome}`
          }
          multiple
          limitTags={2}
          onChange={handleEmployeesSelect}
          placeholder="Selecione o(s) Funcionário(s)"
          renderTags={(value: EmployeeDTO[]) => {
            return value.some(employee => employee.referencia === 0) ? (
              <Chip
                label="Todos"
                onDelete={() => setEmployeesSelected([])}
                color="default"
              />
            ) : (
              value.map(employee => (
                <Chip
                  label={`${employee.numeroCadastro} - ${employee.nome}`}
                  onDelete={(_event: any) =>
                    handleEmployeesTagRemoveSelection(employee)
                  }
                  color="default"
                />
              ))
            );
          }}
          renderOption={(
            option: EmployeeDTO,
            state: AutocompleteRenderOptionState,
          ) => {
            return (
              <div key={option.numeroCadastro}>
                <Checkbox
                  color="primary"
                  icon={<CheckBoxOutlineBlankIcon />}
                  checkedIcon={<CheckBoxIcon />}
                  style={{ marginRight: 8 }}
                  checked={state.selected}
                />
                {`${
                  option.numeroCadastro !== 0
                    ? `${option.numeroCadastro} - `
                    : ''
                }${option.nome}`}
              </div>
            );
          }}
        />
      </Grid>
    </>
  );

  return (
    <CustomDialog
      maxWidth="md"
      fullWidth
      handleClose={handleCloseModal}
      handleConfirm={submit}
      title="Enviar Avisos"
      okButtonLabel="Enviar"
      disabledOkButton={
        noticeText.trim().length === 0 ||
        noticeTitle.trim().length === 0 ||
        (referencesSelected.length === 0 && employeesSelected.length === 0)
      }
      contentDividers
      open={open}
    >
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={3}>
          <Typography
            className={classes.fieldLabelText}
            color="textPrimary"
            variant="body1"
          >
            Enviar para:
          </Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={sendingType}
              onChange={(_event, value) => {
                setEmployeesSelected([]);
                setReferencesSelected([]);
                setSendingType(value);
              }}
            >
              <FormControlLabel
                value={EMPLOYEES_SENDING_TYPE}
                control={<Radio color="primary" />}
                label="Funcionários"
              />
              <FormControlLabel
                value={CONDOMINIUM_SENDING_TYPE}
                control={<Radio color="primary" />}
                label="Condomínios"
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        {sendingType === CONDOMINIUM_SENDING_TYPE
          ? selectCondominiumsField
          : selectEmployeesField}

        <Grid item xs={12} sm={3}>
          <Typography
            className={classes.fieldLabelText}
            color="textPrimary"
            variant="body1"
          >
            Título:
          </Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Insira o título do aviso"
            value={noticeTitle}
            onChange={event => setNoticeTitle(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Typography
            className={classes.fieldLabelText}
            color="textPrimary"
            variant="body1"
          >
            Aviso:
          </Typography>
        </Grid>
        <Grid item xs={12} sm={9}>
          <TextField
            placeholder="Insira o conteúdo da mensagem de aviso"
            multiline
            fullWidth
            rows={4}
            variant="outlined"
            size="small"
            value={noticeText}
            onChange={event => setNoticeText(event.target.value)}
          />
        </Grid>
      </Grid>
    </CustomDialog>
  );
};

export default SendNoticesDialog;
